import { zodResolver } from "@hookform/resolvers/zod";
import {
  Grid,
  TextField,
  FormControlLabel,
  RadioGroup,
  Radio,
  FormControl,
  Tabs,
  Tab,
} from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import UiButton from "components/base/UiButton";
import UiTypography from "components/base/UiTypography";
import { Product, ProductMeasurementJob } from "generated/graphql";
import useModalState from "hooks/useModalState";
import AutoComplete from "modules/AutoComplete/AutoComplete";
import AutoCompleteWithSelections from "modules/AutoComplete/AutoCompleteWithSelections";
import Modal from "modules/Modal/Modal";
import MapModal from "modules/Mapmodal/MapModal";
import { Controller, useForm } from "react-hook-form";
import React, { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { z } from "zod";
import ProductOptions from "../ProductOptions";
import ProductGroupOptions from "modules/productGroups/ProductGroupOptions";
import { MapOutput } from "interfaces/interfaces";
import measurementMapTranslations from "constants/measurementMapTranslation";
import useGetOrganizationSettings from "hooks/organization/useGetOrganizationSettings";
import { drawerTabStyles } from "modules/proposals/draft/drawerTabStyles";

export interface SelectProductValues {
  selectionType: "products" | "productGroup";
  productIds: Product["id"][];
  productGroupId?: string;
}

export interface ProductMeasurement {
  clientId: string;
  id: string;
  label: string;
  measurementJob?: string | null;
  measurementMaterial?: string | null;
  jobSize: string | number;
  mapData?: string | null;
  productionRate?: string;
  dailyUnitsComplete?: string;
  jobDuration?: string;
  sourceField?: "dailyUnitsComplete" | "jobDuration";
  defaultDailyUnitsComplete?: string;
}

interface SelectedProductInstance {
  selectionKey: string;
  productId: string;
  label: string;
  measurementJob?: string | null;
  measurementMaterial?: string | null;
  dailyUnitsComplete?: string | number | null;
}

export interface SelectProductFormProps {
  onSubmit: (measurements: ProductMeasurement[]) => Promise<unknown>;
  loading: boolean;
  mapData?: string;
  hideSelectionLabel?: boolean;
  useNewDesign?: boolean;
}

export const schema = z
  .object({
    selectionType: z.enum(["products", "productGroup"]),
    productIds: z.array(z.string()).optional(),
    productGroupId: z.string().optional(),
  })
  .refine(
    (data) => {
      if (data.selectionType === "products") {
        return data.productIds && data.productIds.length > 0;
      } else {
        return data.productGroupId;
      }
    },
    {
      message: "Please select products or a product group",
      path: ["selectionType"],
    }
  );

const newDesignAutocompleteSx = {
  "& .MuiAutocomplete-root": {
    "& .MuiOutlinedInput-root": {
      borderRadius: "8px",
      minHeight: "36px",
      height: "36px",
      "& .MuiOutlinedInput-input": {
        padding: "7px 12px",
        fontSize: "12px",
      },
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: "#E0E3E8",
      },
    },
    "& .MuiInputLabel-root": {
      fontSize: "12px",
      color: "#7B809A",
      top: 0,
      transform: "translate(12px, 8px) scale(1)",
      lineHeight: 1.2,
      "&.MuiInputLabel-shrink": {
        transform: "translate(12px, -7px) scale(0.85)",
      },
    },
  },
} as const;

export default function SelectProductForm({
  onSubmit,
  loading,
  mapData,
  hideSelectionLabel,
  useNewDesign,
}: SelectProductFormProps) {
  const { open: modalOpen, onOpen: openModal, onClose: closeModal } = useModalState();
  const { open: mapModalOpen, onOpen: openMapModal, onClose: closeMapModal } = useModalState();
  const [measurements, setMeasurements] = useState<ProductMeasurement[]>([]);
  const [selectedProductInstances, setSelectedProductInstances] = useState<
    SelectedProductInstance[]
  >([]);
  const [currentMeasuringProduct, setCurrentMeasuringProduct] = useState<{
    measurementId: string;
    measurementJob: string;
  } | null>(null);
  const productGroupsRef = React.useRef<any[]>([]);
  const [loadingMessageIndex, setLoadingMessageIndex] = useState(0);
  const { data } = useGetOrganizationSettings();
  const averageWorkingHoursPerDay = +(data?.["average_working_hours_per_day"] ?? 8);
  const selectionCounterRef = useRef(0);

  const loadingMessages = [
    "Processing products...",
    "Validating selections...",
    "Adding to proposal...",
  ];

  const methods = useForm<SelectProductValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      selectionType: "products",
      productIds: [],
      productGroupId: "",
    },
  });

  const selectionType = methods.watch("selectionType") as "products" | "productGroup";
  const selectedProductIds = methods.watch("productIds") || [];
  const selectedProductGroupId = methods.watch("productGroupId");

  const renderProductOptions = useCallback(
    ({ productOptions, loading, search }) => {
      return (
        <Controller
          name="productIds"
          control={methods.control}
          render={({ field, fieldState: { error } }) => (
            <AutoCompleteWithSelections
              options={productOptions}
              field={field}
              loading={loading}
              error={error?.message ? error : ""}
              label="Select Product(s)"
              variant={useNewDesign ? "outlined" : "standard"}
              disableCloseOnSelect={true}
              search={search}
              selectedItemsPlacement="top"
              selectedItems={selectedProductInstances.map((selection, index) => ({
                value: selection.productId,
                label: selection.label,
                selectionKey: selection.selectionKey,
                selectionIndex: index,
              }))}
              onSelectOption={(option) => {
                const productId = String(option.value);
                const nextSelectionKey = `${productId}-${selectionCounterRef.current++}`;
                const nextSelections = [
                  ...selectedProductInstances,
                  {
                    selectionKey: nextSelectionKey,
                    productId,
                    label: option.label,
                    measurementJob: (option as any).measurementJob,
                    measurementMaterial: (option as any).measurementMaterial,
                    dailyUnitsComplete: (option as any).dailyUnitsComplete,
                  },
                ];
                setSelectedProductInstances(nextSelections);
                field.onChange(nextSelections.map((selection) => selection.productId));
              }}
              onDeleteSelection={(selectionKey) => {
                const nextSelections = selectedProductInstances.filter(
                  (selection) => selection.selectionKey !== selectionKey
                );
                setSelectedProductInstances(nextSelections);
                field.onChange(nextSelections.map((selection) => selection.productId));
              }}
            />
          )}
        />
      );
    },
    [methods.control, selectedProductInstances, useNewDesign]
  );

  const renderProductGroupOptions = useCallback(
    ({ productGroupOptions, productGroups, loading, search }) => {
      productGroupsRef.current = productGroups;

      return (
        <Controller
          name="productGroupId"
          control={methods.control}
          render={({ field, fieldState: { error } }) => (
            <AutoComplete
              options={productGroupOptions}
              field={field}
              loading={loading}
              error={error?.message ? error : ""}
              label="Select Product Group"
              variant={useNewDesign ? "outlined" : "standard"}
              multiple={false}
            />
          )}
        />
      );
    },
    [methods.control]
  );

  const resolvedProducts = useMemo(() => {
    if (selectionType === "products") {
      return selectedProductInstances.map((selection) => ({
        id: selection.productId,
        value: selection.productId,
        label: selection.label,
        measurementJob: selection.measurementJob,
        measurementMaterial: selection.measurementMaterial,
        dailyUnitsComplete: selection.dailyUnitsComplete,
        selectionKey: selection.selectionKey,
      }));
    }
    if (selectionType === "productGroup" && selectedProductGroupId) {
      const selectedGroup = productGroupsRef.current.find(
        (group) => group.id === selectedProductGroupId
      );
      return (
        selectedGroup?.products.map((p: any) => ({
          ...p,
          value: p.id,
          label: p.name,
        })) || []
      );
    }
    return [];
  }, [selectionType, selectedProductGroupId, selectedProductInstances]);

  const resolvedProductIds = useMemo(
    () => resolvedProducts.map((p) => p.value),
    [resolvedProducts]
  );

  useEffect(() => {
    if (loading) {
      const interval = setInterval(() => {
        setLoadingMessageIndex((prev) => {
          const nextIndex = prev + 1;
          return nextIndex >= loadingMessages.length ? loadingMessages.length - 1 : nextIndex;
        });
      }, 1000);

      return () => clearInterval(interval);
    } else {
      setLoadingMessageIndex(0);
    }
  }, [loading, loadingMessages.length]);

  useEffect(() => {
    const newMeasurements = measurements.map((measurement) => {
      const jobSize = parseFloat(measurement.jobSize.toString() || "0");
      const newMeasurement = { ...measurement };
      let changed = false;

      if (measurement.sourceField === "dailyUnitsComplete") {
        const dailyUnitsCompleteValue =
          measurement.dailyUnitsComplete || measurement.defaultDailyUnitsComplete;
        const dailyUnitsComplete = parseFloat(dailyUnitsCompleteValue || "0");
        if (dailyUnitsComplete > 0) {
          const newJobDuration = (
            (jobSize / dailyUnitsComplete) *
            averageWorkingHoursPerDay
          ).toFixed(2);
          if (newMeasurement.jobDuration !== newJobDuration) {
            newMeasurement.jobDuration = newJobDuration;
            changed = true;
          }
        }
      } else if (measurement.sourceField === "jobDuration") {
        const jobDuration = parseFloat(measurement.jobDuration || "0");
        if (jobDuration > 0) {
          const newDailyUnitsComplete = (
            (jobSize / jobDuration) *
            averageWorkingHoursPerDay
          ).toFixed(2);
          if (newMeasurement.dailyUnitsComplete !== newDailyUnitsComplete) {
            newMeasurement.dailyUnitsComplete = newDailyUnitsComplete;
            changed = true;
          }
        }
      }

      return changed ? newMeasurement : measurement;
    });

    if (JSON.stringify(newMeasurements) !== JSON.stringify(measurements)) {
      setMeasurements(newMeasurements);
    }
  }, [measurements, averageWorkingHoursPerDay]);

  const handleFormSubmit = async (data: SelectProductValues) => {
    const initialMeasurements: ProductMeasurement[] =
      selectionType === "products"
        ? selectedProductInstances.map((selection) => ({
            clientId: `${Date.now()}-${selectionCounterRef.current++}`,
            id: selection.productId,
            label: selection.label,
            measurementJob: selection.measurementJob,
            measurementMaterial: selection.measurementMaterial,
            jobSize: 0,
            mapData: null,
            dailyUnitsComplete: "",
            jobDuration: "",
            sourceField: undefined,
            defaultDailyUnitsComplete: selection.dailyUnitsComplete?.toString() || "",
          }))
        : resolvedProducts.map((product: any) => {
            const defaultDailyUnitsComplete = product.dailyUnitsComplete?.toString() || "";
            const clientId = `${product.id || product.value}-${selectionCounterRef.current++}`;

            return {
              clientId,
              id: String(product.id || product.value),
              label: product.label,
              measurementJob: product.measurementJob,
              measurementMaterial: product.measurementMaterial,
              jobSize: 0,
              mapData: null,
              dailyUnitsComplete: "",
              jobDuration: "",
              sourceField: undefined,
              defaultDailyUnitsComplete,
            };
          });

    if (resolvedProducts.length === 1) {
      const cleanedMeasurements = initialMeasurements.map((measurement) => {
        return {
          clientId: measurement.clientId,
          id: measurement.id,
          label: measurement.label,
          measurementJob: measurement.measurementJob,
          ...(measurement.measurementMaterial && {
            measurementMaterial: measurement.measurementMaterial,
          }),
          jobSize: measurement.jobSize || 0,
          ...(measurement.mapData && { mapData: measurement.mapData }),
        };
      });
      await onSubmit(cleanedMeasurements);
      methods.reset();
      setSelectedProductInstances([]);
      return;
    }
    setMeasurements(initialMeasurements);
    openModal();
  };

  const handleMeasurementFieldChange = (
    measurementId: string,
    field: keyof ProductMeasurement,
    value: string
  ) => {
    setMeasurements((prev) =>
      prev.map((measurement) => {
        if (measurement.clientId === measurementId) {
          const newMeasurement = { ...measurement, [field]: value };
          if (field === "dailyUnitsComplete") {
            if (value) {
              newMeasurement.sourceField = "dailyUnitsComplete";
              newMeasurement.jobDuration = "";
            } else {
              newMeasurement.sourceField = undefined;
            }
          } else if (field === "jobDuration") {
            if (value) {
              newMeasurement.sourceField = "jobDuration";
              newMeasurement.dailyUnitsComplete = "";
            } else {
              newMeasurement.sourceField = undefined;
            }
          }
          return newMeasurement;
        }
        return measurement;
      })
    );
  };

  const handleMeasureOnMapClick = (measurementId: string, product: ProductMeasurement) => {
    setCurrentMeasuringProduct({
      measurementId,
      measurementJob: product.measurementJob || "",
    });
    openMapModal();
  };

  const saveMapValue = (data: MapOutput, measurementJob: string): number => {
    if (measurementJob?.trim() === ProductMeasurementJob.UNIT) {
      return data?.measurements?.area?.feet ?? data?.measurements?.area?.yards ?? 0;
    }
    const [measurement, unit] =
      measurementMapTranslations[measurementJob?.trim()]?.split(".") || [];
    return data.measurements?.[measurement]?.[unit] ?? 0;
  };

  const handleMapSave = (data: MapOutput) => {
    if (!currentMeasuringProduct) return;

    const jobSizeValue = saveMapValue(data, currentMeasuringProduct.measurementJob);
    const mapDataString = JSON.stringify(data.geojson);

    setMeasurements((prev) =>
      prev.map((measurement) =>
        measurement.clientId === currentMeasuringProduct.measurementId
          ? { ...measurement, jobSize: jobSizeValue.toString(), mapData: mapDataString }
          : measurement
      )
    );

    closeMapModal();
    setCurrentMeasuringProduct(null);
  };

  const handleFinalSubmit = async () => {
    const cleanedMeasurements = measurements.map((measurement) => {
      const payload: any = {
        id: measurement.id,
        jobSize: measurement.jobSize || 0,
        ...(measurement.mapData && { mapData: measurement.mapData }),
      };

      if (measurement.sourceField === "dailyUnitsComplete") {
        payload.dailyUnitsComplete =
          measurement.dailyUnitsComplete || measurement.defaultDailyUnitsComplete || "0";
        payload.jobDuration = null;
      } else if (measurement.sourceField === "jobDuration") {
        payload.jobDuration = measurement.jobDuration || "0";
        payload.dailyUnitsComplete = null;
      }

      return payload;
    });
    await onSubmit(cleanedMeasurements);
    closeModal();
    methods.reset();
    setMeasurements([]);
    setSelectedProductInstances([]);
  };

  const getCurrentMeasurement = (productId: string | number) => {
    return measurements.find((measurement) => measurement.clientId === String(productId));
  };

  const tabIndex = selectionType === "products" ? 0 : 1;
  const handleTabChange = (_: unknown, value: number) => {
    methods.setValue("selectionType", value === 0 ? "products" : "productGroup");
  };

  const submitButton = useNewDesign ? (
    <UiButton
      uiVariant="primary-green"
      type="submit"
      disabled={loading || resolvedProductIds.length === 0}
      sx={{ minHeight: "36px", height: "36px", px: "16px", py: 0 }}
    >
      {loading ? loadingMessages[loadingMessageIndex] : "Add to proposal"}
    </UiButton>
  ) : (
    <MDButton
      type="submit"
      disabled={loading || resolvedProductIds.length === 0}
      color="info"
      variant="gradient"
    >
      {loading ? loadingMessages[loadingMessageIndex] : "Add to Proposal"}
    </MDButton>
  );

  return (
    <>
      <MDBox
        component="form"
        role="form"
        onSubmit={methods.handleSubmit(handleFormSubmit)}
        {...(!useNewDesign && { p: 3 })}
      >
        {useNewDesign ? (
          <MDBox display="flex" flexDirection="column" gap="16px">
            <Tabs value={tabIndex} onChange={handleTabChange} sx={drawerTabStyles}>
              <Tab label="Select individual products" />
              <Tab label="Select product group" />
            </Tabs>

            <MDBox
              display="flex"
              alignItems="flex-start"
              gap="12px"
              flexWrap={{ xs: "wrap", md: "nowrap" }}
            >
              <MDBox
                className="tour-select-product"
                sx={{ ...newDesignAutocompleteSx, flex: 1, minWidth: 0 }}
              >
                {selectionType === "products" ? (
                  <ProductOptions>{renderProductOptions}</ProductOptions>
                ) : (
                  <ProductGroupOptions>{renderProductGroupOptions}</ProductGroupOptions>
                )}
              </MDBox>

              <MDBox
                className="tour-add-product-to-proposal"
                sx={{
                  flexShrink: 0,
                  width: { xs: "100%", md: "auto" },
                  "& > *": { width: { xs: "100%", md: "auto" } },
                }}
              >
                {submitButton}
              </MDBox>
            </MDBox>
          </MDBox>
        ) : (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Controller
                name="selectionType"
                control={methods.control}
                render={({ field }) => (
                  <FormControl component="fieldset">
                    <RadioGroup {...field} row>
                      {!hideSelectionLabel && (
                        <MDTypography sx={{ mr: 2, alignSelf: "center", fontSize: "1rem" }}>
                          Add products to proposal
                        </MDTypography>
                      )}
                      <FormControlLabel
                        value="products"
                        control={<Radio />}
                        label="Select Individual Products"
                      />
                      <FormControlLabel
                        value="productGroup"
                        control={<Radio />}
                        label="Select Product Group"
                      />
                    </RadioGroup>
                  </FormControl>
                )}
              />
            </Grid>

            {selectionType === "products" && (
              <Grid item xs={12}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={4}>
                    <ProductOptions>{renderProductOptions}</ProductOptions>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    {submitButton}
                  </Grid>
                </Grid>
              </Grid>
            )}

            {selectionType === "productGroup" && (
              <Grid item xs={12}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={4}>
                    <ProductGroupOptions>{renderProductGroupOptions}</ProductGroupOptions>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    {submitButton}
                  </Grid>
                </Grid>
              </Grid>
            )}
          </Grid>
        )}
      </MDBox>

      <Modal
        open={modalOpen}
        onClose={closeModal}
        styles={{
          width: "800px",
          height: "auto",
          maxHeight: "80vh",
        }}
      >
        <MDBox display="flex" alignItems="center" p={2} borderBottom="1px solid #e0e0e0">
          <MDTypography variant="h6" fontWeight="medium">
            Add Measurements
          </MDTypography>
        </MDBox>

        <MDBox p={3}>
          <Grid container spacing={2} alignItems="center" sx={{ mt: 2 }}>
            <Grid item xs={3}>
              <MDTypography variant="button" fontWeight="bold">
                Product
              </MDTypography>
            </Grid>
            <Grid item xs={2}>
              <MDTypography variant="button" fontWeight="bold">
                Job Size
              </MDTypography>
            </Grid>
            <Grid item xs={2}>
              <MDTypography variant="button" fontWeight="bold">
                Production Rate
              </MDTypography>
            </Grid>
            <Grid item xs={2}>
              <MDTypography variant="button" fontWeight="bold">
                Hours
              </MDTypography>
            </Grid>
            <Grid item xs={3}>
              <MDTypography variant="button" fontWeight="bold">
                Map
              </MDTypography>
            </Grid>

            {measurements.map((measurement) => {
              const jobSize = parseFloat(measurement?.jobSize.toString() || "0");
              const dailyUnitsCompleteValue =
                measurement?.dailyUnitsComplete || measurement?.defaultDailyUnitsComplete;
              const dailyUnitsComplete = parseFloat(dailyUnitsCompleteValue || "0");

              const hoursPlaceholder =
                !measurement?.sourceField && jobSize > 0 && dailyUnitsComplete > 0
                  ? ((jobSize / dailyUnitsComplete) * averageWorkingHoursPerDay).toFixed(2)
                  : "0";

              return (
                <React.Fragment key={measurement.clientId}>
                  <Grid item xs={3}>
                    <MDTypography variant="body2">{measurement.label}</MDTypography>
                  </Grid>
                  <Grid item xs={2}>
                    <TextField
                      variant="standard"
                      type="text"
                      placeholder="0"
                      value={measurement?.jobSize || ""}
                      onChange={(e) =>
                        handleMeasurementFieldChange(
                          measurement.clientId,
                          "jobSize",
                          e.target.value
                        )
                      }
                      sx={{ width: 100 }}
                    />
                  </Grid>
                  <Grid item xs={2}>
                    <TextField
                      variant="standard"
                      type="text"
                      placeholder={measurement?.defaultDailyUnitsComplete || "0"}
                      value={measurement?.dailyUnitsComplete || ""}
                      disabled={measurement?.sourceField === "jobDuration"}
                      onChange={(e) =>
                        handleMeasurementFieldChange(
                          measurement.clientId,
                          "dailyUnitsComplete",
                          e.target.value
                        )
                      }
                      sx={{ width: 100 }}
                    />
                  </Grid>
                  <Grid item xs={2}>
                    <TextField
                      variant="standard"
                      type="text"
                      placeholder={hoursPlaceholder}
                      disabled={measurement?.sourceField === "dailyUnitsComplete"}
                      value={measurement?.jobDuration || ""}
                      onChange={(e) =>
                        handleMeasurementFieldChange(
                          measurement.clientId,
                          "jobDuration",
                          e.target.value
                        )
                      }
                      sx={{ width: 100 }}
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <MDButton
                      color="info"
                      size="small"
                      onClick={() => handleMeasureOnMapClick(measurement.clientId, measurement)}
                    >
                      Measure on Map
                    </MDButton>
                  </Grid>
                </React.Fragment>
              );
            })}
          </Grid>

          <MDBox display="flex" justifyContent="flex-end" gap={2} mt={3}>
            <MDButton variant="outlined" color="error" onClick={closeModal}>
              Cancel
            </MDButton>
            <MDButton
              variant="gradient"
              color="success"
              onClick={handleFinalSubmit}
              disabled={loading}
            >
              {loading ? loadingMessages[loadingMessageIndex] : "Add to Proposal"}
            </MDButton>
          </MDBox>
        </MDBox>
      </Modal>

      {currentMeasuringProduct && (
        <MapModal
          initialArea={
            getCurrentMeasurement(currentMeasuringProduct.measurementId)?.mapData
              ? JSON.parse(
                  getCurrentMeasurement(currentMeasuringProduct.measurementId)?.mapData || "{}"
                )
              : mapData
              ? JSON.parse(mapData)
              : null
          }
          open={mapModalOpen}
          onClose={closeMapModal}
          hideLocation={true}
          showTools={[currentMeasuringProduct.measurementJob as keyof typeof ProductMeasurementJob]}
          onSave={handleMapSave}
          guided={false}
          measureMode={true}
        />
      )}
    </>
  );
}
